import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Clock,
  FileText,
  DollarSign,
  UserPlus,
  Star,
  Heart,
  Ticket,
  Tags,
  Gift,
  TrendingUp,
  MessageSquare,
  Building2,
  Sparkles,
  BarChart3,
  MapPin,
  Rocket,
  Briefcase,
  Shield
} from "lucide-react";
import { CalendarVisualization, WorkflowPhoneMockup, EmployeePortalVisual, DashboardMockup, IntegrationCloud, AIAssistantDemo, ReviewsVisualization, MultiVenueVisual, EmailMarketingVisualization } from "../visualizations";
import { WalletPassPreview, PassVisualContainer } from "../shared";
import { PASS_COLORS, DEMO_PASS_DATA } from "../constants";

interface BentoGridProps {
  containerRef: React.RefObject<HTMLDivElement>;
  manageStartRef: React.RefObject<HTMLDivElement>;
  growStartRef: React.RefObject<HTMLDivElement>;
  businessStartRef: React.RefObject<HTMLDivElement>;
  businessEndRef: React.RefObject<HTMLDivElement>;
}

export const BentoGrid = ({ containerRef, manageStartRef, growStartRef, businessStartRef, businessEndRef }: BentoGridProps) => {
  const [isAiHovering, setIsAiHovering] = useState(false);
  const [isMultiVenueHovering, setIsMultiVenueHovering] = useState(false);
  const [isReviewsHovering, setIsReviewsHovering] = useState(false);

  return (
    <div ref={containerRef} className="relative w-full px-6 lg:px-8">
      {/* Enhanced Bento Grid Container - 6 columns for more flexibility */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 auto-rows-[minmax(140px,auto)] gap-[1px] bg-border rounded-2xl overflow-hidden shadow-2xl bento-stagger max-w-7xl mx-auto">

        {/* ═══════════════════════════════════════════════════════════════
            MANAGE SECTION
            ═══════════════════════════════════════════════════════════════ */}
        {/* MANAGE Header - Large (2 cols, 2 rows) */}
        <div ref={manageStartRef} className="col-span-2 md:col-span-2 lg:col-span-2 row-span-2 p-8 bg-sidebar bento-cell bento-cell-primary relative bento-noise">
          <div className="relative z-10 h-full flex flex-col">
            <span className="text-xs uppercase tracking-[0.25em] text-primary font-medium mb-2">Manage</span>
            <h2 className="bento-section-title text-foreground">
              Your team,
              <span className="block text-primary">organized.</span>
            </h2>
            <p className="bento-section-subtitle mt-4">
              From schedules to payroll—everything in one place.
            </p>
            <div className="mt-auto pt-6">
              <Button className="bg-primary hover:bg-primary/90 group" asChild>
                <Link to="/features/manage">
                  Explore Tools <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </div>
          <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full bg-primary/5" />
        </div>

        {/* Calendar Visual - Large (3 cols, 2 rows) */}
        <div className="group col-span-2 md:col-span-2 lg:col-span-3 row-span-2 bg-sidebar bento-cell bento-cell-primary bento-visual">
          <div className="bento-visual-content">
            <CalendarVisualization />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
            <h3 className="bento-feature-title text-foreground">Smart Scheduling</h3>
            <p className="bento-feature-subtitle">Drag. Drop. Done.</p>
          </div>
        </div>

        {/* Workflow Flow (1 col on lg, spans 4 rows) */}
        <div className="col-span-2 lg:col-span-1 row-span-4 bg-sidebar bento-cell bento-cell-primary">
          <div className="bento-visual-content h-full">
            <WorkflowPhoneMockup />
          </div>
        </div>

        {/* Employee Portal - Dashboard Preview */}
        <div className="col-span-2 row-span-2 bg-sidebar bento-cell bento-cell-primary bento-visual overflow-hidden relative">
          <div className="bento-visual-content">
            <EmployeePortalVisual />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-4 z-10 bg-gradient-to-t from-sidebar via-sidebar/80 to-transparent">
            <h3 className="bento-feature-title text-foreground text-base">Employee Portal</h3>
            <p className="bento-feature-subtitle">Self-service access for your team</p>
          </div>
        </div>

        {/* Payroll - Compact */}
        <div className="col-span-2 lg:col-span-2 p-5 bg-sidebar bento-cell bento-cell-primary relative">
          <DollarSign className="h-8 w-8 text-primary mb-3" />
          <h3 className="bento-feature-title text-foreground">EU Payroll</h3>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-2xl font-bold text-primary">25+</span>
            <p className="bento-feature-subtitle">countries</p>
          </div>
        </div>

        {/* Contracts */}
        <div className="col-span-2 lg:col-span-1 p-5 bg-sidebar bento-cell bento-cell-primary">
          <FileText className="h-8 w-8 text-primary mb-3" />
          <h3 className="bento-feature-title text-foreground">Contracts</h3>
          <p className="bento-feature-subtitle mt-1">Digital & secure.</p>
        </div>

        {/* Hiring - Compact */}
        <div className="col-span-2 lg:col-span-2 p-5 bg-sidebar bento-cell bento-cell-primary">
          <UserPlus className="h-8 w-8 text-primary mb-3" />
          <h3 className="bento-feature-title text-foreground">Hire & Onboard</h3>
          <p className="bento-feature-subtitle mt-1">Post jobs → Track → Onboard.</p>
        </div>

        {/* Time Tracking - Compact */}
        <div className="col-span-2 lg:col-span-1 p-5 bg-sidebar bento-cell bento-cell-primary">
          <Clock className="h-8 w-8 text-primary mb-3" />
          <h3 className="bento-feature-title text-foreground">QR Clock-In</h3>
          <p className="bento-feature-subtitle mt-1">No more paper.</p>
        </div>

        {/* Manage CTA */}
        <div className="col-span-2 md:col-span-4 lg:col-span-6 p-4 bg-sidebar bento-cell bento-cell-primary flex items-center justify-between">
          <span className="text-sm text-muted-foreground">+ Attendance tracking, shift requests, performance & more</span>
          <Link to="/features/manage" className="text-primary hover:underline text-sm font-medium flex items-center gap-1">
            See all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        {/* ═══════════════════════════════════════════════════════════════
            GROW SECTION
            ═══════════════════════════════════════════════════════════════ */}
        {/* GROW Header */}
        <div ref={growStartRef} className="col-span-2 row-span-2 p-8 bg-sidebar bento-cell bento-cell-grow relative bento-noise">
          <div className="relative z-10 h-full flex flex-col">
            <span className="text-xs uppercase tracking-[0.25em] text-grow font-medium mb-2">Grow</span>
            <h2 className="bento-section-title text-foreground">
              Turn visitors
              <span className="block text-grow">into loyalists.</span>
            </h2>
            <p className="bento-section-subtitle mt-4">
              Build lasting customer relationships.
            </p>
            <div className="mt-auto pt-6">
              <Button className="bg-grow hover:bg-grow/90 group" asChild>
                <Link to="/features/grow">
                  Grow Now <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </div>
          <div className="absolute -left-8 -bottom-8 w-32 h-32 rounded-full bg-grow/5" />
        </div>

        {/* Loyalty Visual */}
        <div className="group/loyalty col-span-2 lg:col-span-2 row-span-2 bg-sidebar bento-cell bento-cell-grow bento-visual relative">
          <div className="bento-visual-content">
            <PassVisualContainer
              groupHoverClass="group-hover/loyalty"
              leftPass={
                <WalletPassPreview
                  platform="apple"
                  type="loyalty"
                  businessName={DEMO_PASS_DATA.loyalty.businessName}
                  businessIcon={DEMO_PASS_DATA.loyalty.businessIcon}
                  heroImage={DEMO_PASS_DATA.loyalty.heroImageApple}
                  currentStamps={DEMO_PASS_DATA.loyalty.currentStamps}
                  totalStamps={DEMO_PASS_DATA.loyalty.totalStamps}
                  rewardText={DEMO_PASS_DATA.loyalty.rewardText}
                  backgroundColor={PASS_COLORS.loyalty}
                />
              }
              rightPass={
                <WalletPassPreview
                  platform="google"
                  type="loyalty"
                  businessName={DEMO_PASS_DATA.loyalty.businessName}
                  businessIcon={DEMO_PASS_DATA.loyalty.businessIcon}
                  heroImage={DEMO_PASS_DATA.loyalty.heroImageGoogle}
                  currentStamps={DEMO_PASS_DATA.loyalty.currentStamps}
                  totalStamps={DEMO_PASS_DATA.loyalty.totalStamps}
                  backgroundColor={PASS_COLORS.loyalty}
                />
              }
            />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
            <h3 className="bento-feature-title text-foreground">Loyalty Programs</h3>
            <p className="bento-feature-subtitle">Stamp cards & rewards</p>
          </div>
        </div>

        {/* Coupons Visual */}
        <div className="group/coupon col-span-2 lg:col-span-2 row-span-2 bg-sidebar bento-cell bento-cell-grow bento-visual relative">
          <div className="bento-visual-content">
            <PassVisualContainer
              groupHoverClass="group-hover/coupon"
              leftPass={
                <WalletPassPreview
                  platform="apple"
                  type="coupon"
                  businessName={DEMO_PASS_DATA.coupon.businessName}
                  businessIcon={DEMO_PASS_DATA.coupon.businessIcon}
                  heroImage={DEMO_PASS_DATA.coupon.heroImageApple}
                  discountText={DEMO_PASS_DATA.coupon.discountText}
                  discountSubtext={DEMO_PASS_DATA.coupon.discountSubtext}
                  validUntil={DEMO_PASS_DATA.coupon.validUntil}
                  backgroundColor={PASS_COLORS.coupon}
                />
              }
              rightPass={
                <WalletPassPreview
                  platform="google"
                  type="coupon"
                  businessName={DEMO_PASS_DATA.coupon.businessName}
                  businessIcon={DEMO_PASS_DATA.coupon.businessIcon}
                  heroImage={DEMO_PASS_DATA.coupon.heroImageGoogle}
                  discountText={DEMO_PASS_DATA.coupon.discountText}
                  validUntil={DEMO_PASS_DATA.coupon.validUntil}
                  backgroundColor={PASS_COLORS.coupon}
                />
              }
            />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
            <h3 className="bento-feature-title text-foreground">Coupons</h3>
            <p className="bento-feature-subtitle">One-time rewards</p>
          </div>
        </div>

        {/* Reviews - Smart Funnels Visual */}
        <div
          className="col-span-2 lg:col-span-3 row-span-2 bg-sidebar bento-cell bento-cell-grow bento-visual relative overflow-hidden"
          onMouseEnter={() => setIsReviewsHovering(true)}
          onMouseLeave={() => setIsReviewsHovering(false)}
        >
          <div className="bento-visual-content">
            <ReviewsVisualization isHovering={isReviewsHovering} />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-4 z-10 bg-gradient-to-t from-sidebar via-sidebar/80 to-transparent">
            <h3 className="bento-feature-title text-foreground">Smart Review Funnels</h3>
            <p className="bento-feature-subtitle">Unhappy customers complain fast. Happy ones need a nudge. Fully configurable.</p>
          </div>
        </div>

        {/* Email Marketing - Visual */}
        <div className="col-span-2 lg:col-span-2 row-span-2 bg-sidebar bento-cell bento-cell-grow bento-visual relative">
          <div className="bento-visual-content">
            <EmailMarketingVisualization />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-4 z-10 bg-gradient-to-t from-sidebar via-sidebar/80 to-transparent">
            <h3 className="bento-feature-title text-foreground">Email Marketing</h3>
            <p className="bento-feature-subtitle">Templates, automations & analytics</p>
          </div>
        </div>

        {/* Grow CTA */}
        <div className="col-span-2 md:col-span-4 lg:col-span-6 p-4 bg-sidebar bento-cell bento-cell-grow flex items-center justify-between">
          <span className="text-sm text-muted-foreground">+ Review funnels, SMS campaigns, social media & more</span>
          <Link to="/features/grow" className="text-grow hover:underline text-sm font-medium flex items-center gap-1">
            See all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        {/* ═══════════════════════════════════════════════════════════════
            BUSINESS SECTION
            ═══════════════════════════════════════════════════════════════ */}
        {/* BUSINESS Header */}
        <div ref={businessStartRef} className="col-span-2 row-span-2 p-8 bg-sidebar bento-cell bento-cell-secondary relative bento-noise">
          <div className="relative z-10 h-full flex flex-col">
            <span className="text-xs uppercase tracking-[0.25em] text-secondary font-medium mb-2">Business</span>
            <h2 className="bento-section-title text-foreground">
              Insights that
              <span className="block text-secondary">drive growth.</span>
            </h2>
            <p className="bento-section-subtitle mt-4">
              Data-driven decisions for smarter operations.
            </p>
            <div className="mt-auto pt-6">
              <Button className="bg-secondary hover:bg-secondary/90 group" asChild>
                <Link to="/features/business">
                  View Insights <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </div>
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-secondary/5" />
        </div>

        {/* Dashboard Mockup */}
        <div className="col-span-2 lg:col-span-3 row-span-2 bg-sidebar bento-cell bento-cell-secondary bento-visual relative">
          <div className="bento-visual-content">
            <DashboardMockup />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-6 z-10 bg-gradient-to-t from-sidebar via-sidebar/80 to-transparent">
            <h3 className="bento-feature-title text-foreground">Analytics</h3>
            <p className="bento-feature-subtitle">Real-time business metrics</p>
          </div>
        </div>

        {/* Integration Cloud */}
        <div className="col-span-2 lg:col-span-1 row-span-2 p-6 bg-sidebar bento-cell bento-cell-secondary flex flex-col">
          <div className="bento-visual-content flex-1">
            <IntegrationCloud />
          </div>
          <div className="mt-auto">
            <h3 className="bento-feature-title text-foreground">Integrations</h3>
            <p className="bento-feature-subtitle mt-1">Connect your tools.</p>
          </div>
        </div>

        {/* AI Assistant Cell */}
        <div
          className="col-span-2 row-span-2 p-6 bg-gradient-to-br from-sidebar to-secondary/10 bento-cell bento-cell-secondary relative overflow-hidden flex flex-col"
          onMouseEnter={() => setIsAiHovering(true)}
          onMouseLeave={() => setIsAiHovering(false)}
        >
          <MessageSquare className="h-10 w-10 text-secondary mb-4" />
          <h3 className="bento-feature-title text-foreground text-xl">AI Assistant</h3>
          <p className="bento-feature-subtitle mt-2">
            Manage Tag-Inn with plain English. Create shifts, check schedules, run reports—all via chat.
          </p>
          <div className="bento-visual-content">
            <AIAssistantDemo isHovering={isAiHovering} />
          </div>
        </div>

        {/* Multi-Venue Management */}
        <div
          className="col-span-2 lg:col-span-2 row-span-2 bg-sidebar bento-cell bento-cell-secondary bento-visual relative"
          onMouseEnter={() => setIsMultiVenueHovering(true)}
          onMouseLeave={() => setIsMultiVenueHovering(false)}
        >
          <div className="bento-visual-content">
            <MultiVenueVisual isHovering={isMultiVenueHovering} />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-4 z-10 bg-gradient-to-t from-sidebar via-sidebar/80 to-transparent">
            <h3 className="bento-feature-title text-foreground">Multi-Venue</h3>
            <p className="bento-feature-subtitle">Share staff across locations</p>
          </div>
        </div>

        {/* Reports */}
        <div className="col-span-2 lg:col-span-2 p-5 bg-sidebar bento-cell bento-cell-secondary">
          <BarChart3 className="h-8 w-8 text-secondary mb-3" />
          <h3 className="bento-feature-title text-foreground">Reports</h3>
          <p className="bento-feature-subtitle mt-1">Custom exportable reports.</p>
        </div>

        {/* Compliance */}
        <div className="col-span-2 lg:col-span-2 p-5 bg-sidebar bento-cell bento-cell-secondary">
          <Shield className="h-8 w-8 text-secondary mb-3" />
          <h3 className="bento-feature-title text-foreground">Compliance</h3>
          <p className="bento-feature-subtitle mt-1">GDPR & local regulations.</p>
        </div>

        {/* Business CTA */}
        <div ref={businessEndRef} className="col-span-2 md:col-span-4 lg:col-span-6 p-4 bg-sidebar bento-cell bento-cell-secondary flex items-center justify-between">
          <span className="text-sm text-muted-foreground">+ Financial forecasting, competitor analysis & more</span>
          <Link to="/features/business" className="text-secondary hover:underline text-sm font-medium flex items-center gap-1">
            See all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};
